import type { AnswerMap, PhaseInfo, WorkflowStage } from '../../types/workflow'
import type { GuidanceResult } from '../../lib/guidance-engine'
import { calculateAge } from '@/lib/dateUtils';

interface TherapyPreviewTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  guidanceData: GuidanceResult
  currentPhase?: PhaseInfo | null
  onContinue?: () => void
}

export default function TherapyPreviewTemplate({
  answers,
  onContinue,
}: TherapyPreviewTemplateProps) {
  const bmiData = answers.bmi_data as { weight_lbs?: number; height_feet?: number; height_inches?: number } | undefined
  const weight = bmiData?.weight_lbs || (answers.weight_lbs as number | undefined)
  const heightFeet = bmiData?.height_feet || (answers.height_feet as number | undefined)
  const heightInches = bmiData?.height_inches || (answers.height_inches as number | undefined) || 0
  const ageFromAnswers = answers.age as number | undefined

  const calculateAgeFromDob = (dobText?: string): number | undefined => {
    if (!dobText) return undefined
    return calculateAge(dobText) ?? undefined
  }

  const ageFromDob = calculateAgeFromDob(answers.date_of_birth as string | undefined)
  const age = ageFromAnswers ?? ageFromDob

  const weightText = weight ? `${weight} lbs` : '—'
  const heightText = heightFeet ? `${heightFeet}′${heightInches}″` : '—'
  const ageText = age ? `${age}` : '—'

  const goalLabels =
    ((answers.goals_selected_labels as string[] | undefined) || []).filter(Boolean).slice(0, 3)
  const primaryGoal = (answers.primary_goal as string | undefined) || ''
  const allGoalLabels = goalLabels.length > 0 ? goalLabels : primaryGoal ? [primaryGoal] : []

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="mb-4">
        <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ww-text-3)]">What you&apos;ve shared</p>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { label: 'Age', value: ageText },
          { label: 'Weight', value: weightText },
          { label: 'Height', value: heightText },
        ].map((item) => (
          <div
            key={item.label}
            className="flex flex-col gap-1 rounded-xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass px-2.5 py-3 text-center"
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--ww-text-3)]">{item.label}</span>
            <span className="font-secondary text-xl text-[var(--ww-text)]">{item.value}</span>
          </div>
        ))}
      </div>

      {allGoalLabels.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {allGoalLabels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center rounded-full border border-[var(--ww-accent)] px-3 py-1.5 text-[12px] font-medium text-[var(--ww-accent)]"
              style={{ backgroundColor: 'rgba(74,138,154,0.12)' }}
            >
              {label}
            </span>
          ))}
          {(answers.goals_selected as string[] | undefined)?.length &&
            (answers.goals_selected as string[]).length > 3 && (
              <span className="inline-flex items-center rounded-full border border-[var(--ww-border)] bg-[var(--ww-surface)] px-3 py-1.5 text-[12px] font-medium text-[var(--ww-text-3)]">
                +{(answers.goals_selected as string[]).length - 3} more
              </span>
            )}
        </div>
      )}

      <div className="rounded-xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass p-4">
        <p className="m-0 font-secondary text-[15px] text-[var(--ww-text)]">You&apos;re in good hands.</p>
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={() => onContinue?.()}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:bg-[var(--ww-btn-hover)] hover:-translate-y-0.5"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
